import {EventEmitter, inject, Injectable} from '@angular/core';
import {AzureService} from './azure.service';
import {TokenRequestData} from '../../models/msal/token-request-data.model';
import {AccessTokenData} from '../../models/msal/access-token-data.model';
import {DefaultWebViewOptions, InAppBrowser} from '@capacitor/inappbrowser';
import {STATIC_CONFIGS} from '../../../configs/static.config';
import {PkceChallenge} from '../../models/msal/pkce-challenge.model';
import {AzureCapacitorLocalStorage} from '../../models/msal/azure-capacitor-local-storage.model';
import {SentryService} from '../sentry/sentry.service';
import {TranslateService} from '@ngx-translate/core';
import {SignInData} from '../../models/msal/sign-in-data.model';
import {SystemInfoService} from '../system-info/system-info.service';
import {AzureSignInMethodEnum} from '../../enums/azure-sign-in-method.enum';

@Injectable({
    providedIn: 'root'
})
export class AzureCapacitorService implements AzureService {
    public readonly onSignIn = new EventEmitter<SignInData>(true);
    public readonly onSilentSignIn = new EventEmitter<SignInData | undefined>(true);
    public readonly onSignInFailure = new EventEmitter<AzureSignInMethodEnum>(true);
    private readonly redirectUri: string;
    private isInAppBrowserOpen = false;
    private hasAuthenticationCallFinished = false;
    private tokenRequestData: TokenRequestData | null = null;
    private accessTokenData: AccessTokenData | null = null;
    private pkceChallenge: PkceChallenge | null = null;
    private azureLocalStorageData: AzureCapacitorLocalStorage | null = null;
    private readonly translateService = inject(TranslateService);
    private readonly sentryService = inject(SentryService);
    private readonly systemInfoService = inject(SystemInfoService);

    public constructor() {
        this.redirectUri = this.systemInfoService.isAndroid() ? STATIC_CONFIGS.msal.androidRedirectUri : STATIC_CONFIGS.msal.iosRedirectUri;
    }

    public async setup(): Promise<void> {
        await this.inAppBrowserCleanUp();
        this.loadTokenDataToLocalStorage();
    }

    public login(): Promise<void> {
        const getTokenPromise: Promise<TokenRequestData> = new Promise(async (resolve, reject) => {
            this.pkceChallenge = this.getPkceChallenge();
            const azureAuthUrl: URL = new URL(STATIC_CONFIGS.msal.codeUri);
            azureAuthUrl.searchParams.set('response_type', 'code');
            azureAuthUrl.searchParams.set('client_id', STATIC_CONFIGS.msal.clientId);
            azureAuthUrl.searchParams.set('redirect_uri', this.redirectUri);
            azureAuthUrl.searchParams.set('code_challenge', this.pkceChallenge?.codeChallenge);
            azureAuthUrl.searchParams.set('code_challenge_method', 'plain');
            azureAuthUrl.searchParams.set('scope', 'user.read');
            azureAuthUrl.searchParams.set('prompt', 'login');
            const lastUsername = this.azureLocalStorageData?.username || null;
            if (lastUsername !== null) {
                azureAuthUrl.searchParams.set('login_hint', lastUsername);
            }

            await InAppBrowser.addListener('browserClosed', () => {
                this.isInAppBrowserOpen = false;
                if (!this.hasAuthenticationCallFinished) {
                    reject(true);
                }
            });

            await InAppBrowser.addListener('browserPageNavigationCompleted', async (data) => {
                const response = data as { url: string };
                const responseUrl = response.url + '';
                if (responseUrl.includes(this.redirectUri)) {
                    try {
                        const url = new URL(responseUrl);
                        const code = url.searchParams.get('code');
                        this.hasAuthenticationCallFinished = true;
                        if (code) {
                            await this.inAppBrowserCleanUp();
                            const token: TokenRequestData | null = await this.getTokenFromCode(code);
                            if (token === null) {
                                reject(false);
                            } else {
                                resolve(token);
                            }
                        } else if (url.searchParams.has('error_description')) {
                            await this.inAppBrowserCleanUp();
                            this.sentryService.captureException(url.searchParams.get('error_description'));
                            reject(false);
                        }
                    } catch (e) {
                        await this.inAppBrowserCleanUp();
                        this.sentryService.captureException(e);
                        reject(false);
                    }
                }
            });

            InAppBrowser.openInWebView({
                    url: azureAuthUrl.toString(),
                    options: {
                        ...DefaultWebViewOptions,
                        showURL: false,
                        showNavigationButtons: false,
                        closeButtonText: this.translateService.instant('LOGIN_EXTERNAL_CLOSE_BUTTON')
                    }
                })
                .then(() => {
                    this.isInAppBrowserOpen = true;
                });
        });
        getTokenPromise.then((tokenRequestData: TokenRequestData) => {
                this.processTokenRequestData(tokenRequestData);
                this.syncTokenDataToLocalStorage();
                this.completeLogin();
            })
            .catch(async (signInAborted: boolean) => {
                await this.inAppBrowserCleanUp();
                this.onSignInFailure.emit(signInAborted ? AzureSignInMethodEnum.None : AzureSignInMethodEnum.Redirect);
            });
        return Promise.resolve();
    }

    public logout(): Promise<void> {
        this.resetTokenData();
        this.syncTokenDataToLocalStorage();
        return Promise.resolve();
    }

    public async tryToReLogin(): Promise<boolean> {
        this.loadTokenDataToLocalStorage();
        if (this.azureLocalStorageData !== null) {
            const tokenRequestData: TokenRequestData | null = await this.silentLogin();
            if (tokenRequestData) {
                this.processTokenRequestData(tokenRequestData);
                this.syncTokenDataToLocalStorage();
                const username = this.azureLocalStorageData.accessTokenData?.tid;
                const password = this.azureLocalStorageData.tokenRequestData?.access_token;
                if (username && password) {
                    this.onSilentSignIn.emit({
                        username,
                        password
                    });
                    return true;
                }
            } else {
                this.onSignInFailure.emit(AzureSignInMethodEnum.Silent);
                return true;
            }
        }
        this.onSilentSignIn.emit(undefined);
        return false;
    }

    private async silentLogin(): Promise<TokenRequestData | null> {
        let tokenData: TokenRequestData | null = null;
        const tenantId = this.azureLocalStorageData?.accessTokenData?.tid;
        const refreshToken = this.azureLocalStorageData?.tokenRequestData?.refresh_token;
        if (tenantId && refreshToken) {
            const tokenUrl = STATIC_CONFIGS.msal.silentLoginUri.replace('%TENANT_ID%', tenantId);
            const params = new URLSearchParams();
            params.append('client_id', STATIC_CONFIGS.msal.clientId);
            params.append('refresh_token', refreshToken);
            params.append('grant_type', 'refresh_token');

            try {
                const response: Response = await fetch(tokenUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    },
                    body: params
                });

                tokenData = await response.json();
            } catch (e) {
                this.sentryService.captureException(e);
            }
        }
        return tokenData;
    }

    private syncTokenDataToLocalStorage(): void {
        localStorage.setItem(STATIC_CONFIGS.msal.capacitorLocalStorageKey, JSON.stringify(this.azureLocalStorageData));
    }

    private loadTokenDataToLocalStorage(): void {
        const azureCapacitorLocalStorageString: string | null = localStorage.getItem(STATIC_CONFIGS.msal.capacitorLocalStorageKey);
        if (azureCapacitorLocalStorageString === null) {
            this.resetTokenData();
            return;
        }

        try {
            this.azureLocalStorageData = JSON.parse(azureCapacitorLocalStorageString);
            if (!this.azureLocalStorageData) {
                this.resetTokenData();
            }
        } catch {
            this.resetTokenData();
        }
    }

    private resetTokenData(): void {
        this.tokenRequestData = null;
        this.accessTokenData = null;
        this.azureLocalStorageData = null;
    }

    private async getTokenFromCode(code: string): Promise<TokenRequestData | null> {
        let tokenData: TokenRequestData | null = null;
        const tokenUrl = STATIC_CONFIGS.msal.tokenUri;
        const params = new URLSearchParams();
        params.append('client_id', STATIC_CONFIGS.msal.clientId);
        params.append('code_verifier', this.pkceChallenge?.codeVerifier as string);
        params.append('code', code);
        params.append('redirect_uri', this.redirectUri);
        params.append('grant_type', 'authorization_code');

        try {
            const response: Response = await fetch(tokenUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: params
            });

            tokenData = await response.json();
        } catch (e) {
            this.sentryService.captureException(e);
            return null;
        }
        return tokenData;
    }

    private processTokenRequestData(tokenRequestData: TokenRequestData): void {
        try {
            this.tokenRequestData = tokenRequestData;
            this.accessTokenData = tokenRequestData.access_token ? this.parseJWTString(tokenRequestData.access_token) : null;
            const username: string | null = this.azureLocalStorageData?.username || this.accessTokenData?.upn || null;
            this.azureLocalStorageData = {
                accessTokenData: this.accessTokenData,
                tokenRequestData: tokenRequestData,
                username
            };
        } catch (e) {
            this.sentryService.captureException(e);
            this.azureLocalStorageData = {
                accessTokenData: null,
                tokenRequestData: null,
                username: this.azureLocalStorageData?.username || null
            };
        }
    }

    private completeLogin(): void {
        if (this.accessTokenData && this.tokenRequestData?.access_token) {
            this.onSignIn.emit({
                username: this.accessTokenData.tid,
                password: this.tokenRequestData.access_token
            });
        } else {
            this.onSignInFailure.emit(AzureSignInMethodEnum.Redirect);
        }
    }

    private getRandomStringBySize(size: number): string {
        const mask =
            'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let result = '';
        const randomUints = crypto.getRandomValues(new Uint8Array(size));
        for (let i = 0; i < size; i++) {
            // cap the value of the randomIndex to mask.length - 1
            const randomIndex = randomUints[i] % mask.length;
            result += mask[randomIndex];
        }
        return result;
    }

    private getPkceChallenge(): PkceChallenge {
        //Needs to be exactly 43 chars long - see: https://learn.microsoft.com/en-us/entra/identity-platform/v2-oauth2-auth-code-flow
        const length = 43;

        let verifier = this.getRandomStringBySize(length);
        if (verifier.length !== length) {
            this.sentryService.captureException(new Error('PKCE is not ' + length + ' chars long: ' + verifier.length));
            verifier = this.getRandomStringBySize(length);
        }

        return {
            codeVerifier: verifier,
            codeChallenge: verifier,
        };
    }

    private parseJWTString(jwtString: string): AccessTokenData | null {
        const jwtStringParts = jwtString.split('.');
        let payloadMap: AccessTokenData | null = null;
        if (jwtStringParts.length === 3) {
            jwtStringParts.pop(); // signature
            try {
                const payloadObjectAsString = window.atob(jwtStringParts.pop() as string);
                payloadMap = JSON.parse(payloadObjectAsString);
            } catch (e) {
                this.sentryService.captureException(e);
            }
        }
        return payloadMap;
    }

    private async inAppBrowserCleanUp(): Promise<void> {
        if (this.isInAppBrowserOpen) {
            try {
                await InAppBrowser.close();
            } catch {
                // Do nothing - if the browser is already closed this will throw an error, which is useless
            }
        }
        InAppBrowser.removeAllListeners();
        this.hasAuthenticationCallFinished = false;
    }
}
